import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../core/services/auth/auth.service';
import { TwoFactorAtuhModel } from '../../../shared/models/two-factor-auth.model';
import { OnlyNumbersDirective } from '../../../shared/directives/only-numbers.directive';
import { APP_VERSION } from '../../../shared/data/version';
import { ApiVersionService } from '../../../core/services/api/api-version.service';
import { ApiVersionModel } from '../../../shared/models/api-version.model';
import { environment } from '../../../../environments/environment';

@Component({ 
  standalone: true,
  selector: 'app-login',
  imports: [RouterLink,FormsModule,CommonModule,OnlyNumbersDirective, TranslateModule],
  templateUrl: './login.html',
  styleUrl: './login.scss'
})

export class Login implements OnInit, OnDestroy {
  username: string = ''; 
  password: string = ''; 
  errorMessage: string | null = null; 
  rememberMe: boolean = false; 
 
  twoFactorAuth = signal<TwoFactorAtuhModel | null>(null);
  requires2fa: boolean = false;
  message2fa = signal<string>('');
  verificationCode: string = '';

  resendSeconds = 0;
  remainingAttempts: number | null = null;
  messageType: 'success' | 'error' | 'info' = 'info';

  private resendTimer: ReturnType<typeof setInterval> | null = null;

  showPassword = false;
  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  frontVersion = APP_VERSION.version;
  frontBuild = APP_VERSION.build;
  apiVersion = signal<ApiVersionModel | null>(null);

  public environmentClass = `env-${environment.envName}`;
  public frontEnvLabel = `${environment.envName}`;  
  // public frontEnvLabel = `Front ${environment.envName}`;  

  constructor(
    private router: Router, 
    private authService : AuthService,
    private ApiVersionService: ApiVersionService,
    private translate: TranslateService) {

      this.setVersion();
    } 

 
  ngOnInit(): void {
    const storedUsername = this.authService.getTwoFactorUsername();
    const storedToken = this.authService.getTwoFactorToken();

    if (!storedUsername || !storedToken) {
      return;
    }

    this.username = storedUsername;
    this.requires2fa = true;

    const resendAvailableAt =
      this.authService.getTwoFactorResendAvailableAt();

    if (resendAvailableAt) {
      const remainingSeconds = Math.max(
        0,
        Math.ceil(
          (new Date(resendAvailableAt).getTime() - Date.now()) / 1000
        )
      );

      this.startResendCountdown(remainingSeconds);
    }
  }

  setVersion(){ 
    this.ApiVersionService.getVersion().subscribe({
      next: (res) => {
        this.apiVersion.set(res); 
      },
      error: (err) => {
        console.error('Error fetching API version:', err);
      }
    });
  }

  // login() {

  //   if(!this.requires2fa){
  //     this.firstLogin();
  //   }else{
  //     this.authService.verifyCode({ username: this.username, code: this.verificationCode}).subscribe({
  //       next: (res) => {
  //         this.finally(res);
  //       },
  //       error: (err) => {
  //         this.errorMessage = err.error.message;
  //         console.error('Login error:', err);

  //         this.finishProgress(); 
  //       }
  //     });
  //   }
    
  // }

  login(): void {
    if (!this.requires2fa) {
      this.firstLogin();
      return;
    }

    const twoFactorToken = this.authService.getTwoFactorToken();

    if (!twoFactorToken) {
      this.resetTwoFactorFlow(
        'Invalid or expired verification request. Please sign in again.'
      );
      return;
    }

    this.authService.verifyCode({
      username: this.username,
      code: this.verificationCode,
      two_factor_token: twoFactorToken
    }).subscribe({
      next: (res) => {
        this.authService.clearTwoFactorState();
        this.finally(res);
      },
      error: (err) => {
        const response = err?.error ?? {};

        this.errorMessage =
          response.message ??
          'Unable to verify the code. Please try again.';

        this.messageType = 'error';

        if (typeof response.remaining_attempts === 'number') {
          this.remainingAttempts = response.remaining_attempts;
        }

        if (response.requires_login === true) {
          this.resetTwoFactorFlow(
            response.message ??
            'Too many invalid verification attempts. Please sign in again.'
          );
          return;
        }

        if (
          err.status === 401 &&
          response.message?.toLowerCase().includes('expired')
        ) {
          this.resetTwoFactorFlow(
            response.message ??
            'Verification session expired. Please sign in again.'
          );
          return;
        }

        this.finishProgress();
      }
    });
  }

  // resendCode(form: any){
  //   this.requires2fa=false;
  //    this.startLoginDelay(form);
  //   //this.firstLogin()
  // }

  // firstLogin(){
  //    // console.log('Logging in datos:', this.username, this.password); 

  //   if (!this.username || !this.password) {
  //     this.errorMessage = 'Username and password are required.';
  //     this.finishProgress();
  //     return; 
  //   }

  //   this.authService.login({ username: this.username, password: this.password }).subscribe({
  //     next: (res) => {

  //       const data = new TwoFactorAtuhModel(res);
  //       this.twoFactorAuth.set(data);
  //       // console.log("data",data);

  //       if (data.requires_2fa) {
  //       // this.message2fa.set(data.message ?? '');
  //         this.requires2fa = true;
  //         this.errorMessage = data.message ?? null; 
  //         this.finishProgress(); 
  //         return;
  //       }
      
  //     },
  //     error: (err) => {
  //       this.errorMessage = err.error.message;
  //       console.error('Login error:', err);

  //       this.finishProgress(); 
  //     }
  //   });
  // }

  resendCode(): void {
    if (this.resendSeconds > 0 || this.loading || this.preloading) {
      return;
    }

    const username =
      this.authService.getTwoFactorUsername() || this.username;

    const twoFactorToken =
      this.authService.getTwoFactorToken();

    if (!username || !twoFactorToken) {
      this.resetTwoFactorFlow(
        'Invalid or expired verification request. Please sign in again.'
      );
      return;
    }

    this.loading = true;
    this.errorMessage = null;
    //this.remainingAttempts = null;

    this.authService.resendCode({
      username,
      two_factor_token: twoFactorToken
    }).subscribe({
      next: (res) => {
        const data = new TwoFactorAtuhModel(res);

        this.twoFactorAuth.set(data);

        this.errorMessage =
          data.message ??
          'A new verification code has been sent to your email.';

        this.messageType = 'success';
        this.verificationCode = '';
        this.remainingAttempts = data.remaining_attempts ?? 5;

        this.authService.setTwoFactorState(
          username,
          data
        );

        this.startResendCountdown(
          data.retry_after ?? 120
        );

        this.finishProgress();
      },
      error: (err) => {
        const response = err?.error ?? {};

        this.errorMessage =
          response.message ??
          'Unable to resend the verification code.';

        this.messageType = 'error';

        if (
          err.status === 429 &&
          typeof response.retry_after === 'number'
        ) {
          this.startResendCountdown(
            response.retry_after
          );

          const currentState = new TwoFactorAtuhModel({
            resend_available_at:
              response.resend_available_at,
            code_expires_at:
              response.code_expires_at
          });

          this.authService.setTwoFactorState(
            username,
            currentState
          );
        }

        if (
          err.status === 401 &&
          response.message?.toLowerCase().includes('expired')
        ) {
          this.resetTwoFactorFlow(
            this.errorMessage ?? 'Verification session expired. Please sign in again.'
          );
          return;
        }

        this.finishProgress();
      }
    });
  }

  firstLogin(): void {
    if (!this.username || !this.password) {
      this.errorMessage = 'Username and password are required.';
      this.messageType = 'error';
      this.finishProgress();
      return;
    }

    this.authService.login({
      username: this.username,
      password: this.password
    }).subscribe({
      next: (res) => {
        const data = new TwoFactorAtuhModel(res);

        this.twoFactorAuth.set(data);

        if (data.requires_2fa) {
          this.requires2fa = true;
          this.remainingAttempts = null;
          this.verificationCode = '';

          this.errorMessage =
            data.message ??
            'A verification code has been sent to your email.';

          this.messageType = 'success';

          this.authService.setTwoFactorState(
            this.username,
            data
          );

          this.startResendCountdown(
            data.retry_after ?? 0
          );
        }

        this.finishProgress();
      },
      error: (err) => {
        this.errorMessage =
          err?.error?.message ??
          'Unable to sign in. Please try again.';

        this.messageType = 'error';
        this.finishProgress();
      }
    });
  }

  finally(res: any){
    const token = res.access_token;
    const role = 'user'; 

     //console.log("res=",res)

    this.authService.setAuthState(token, role, this.rememberMe); 
    
    // set minimal user
    const reducedUser = {
      id: res.data.id,
      first_name: res.data.first_name,
      last_name: res.data.last_name,
      email: res.data.email,
      profile_picture: res.data.profile_picture || null,
      language: res.data.language || null,
      agency: res.data.agency || null,
      timezone: res.data.timezone ?? null,
      timezone_mode: res.data.timezone_mode ?? null
    };

    // console.log("reduceUser=",reducedUser)

    this.authService.setUser(reducedUser, role, this.rememberMe);

    // 🔹 Aplica el idioma inmediatamente
    this.translate.use(res.data.language?.code || 'en');
    
    // Limpia errores y navega
    this.errorMessage = null;
    this.router.navigate(['/dashboard']);


    this.finishProgress();      // cerrar barra al éxito
  }

  //animacion del progresssbar
  preloading = false;     // barra de 1s antes del login real
  loading = false;
  showBar = false;  
  progress = 0;           // 0 → 100
  private preTimer: any;  // id del setInterval
  private loadTimer: any;

 startLoginDelay(form: any) {
    this.errorMessage = null;
    // if (form.invalid || this.preloading || this.loading) return;
    if (this.preloading || this.loading) return;

    this.showBar = true;
    this.preloading = true;
    this.progress = 0;

    const preMs = 1000;
    const stepMs = 50;
    const incPre = (80 * stepMs) / preMs; // sube hasta ~80% en 1s

    this.preTimer = setInterval(() => {
      this.progress = Math.min(this.progress + incPre, 80);
    }, stepMs);

    setTimeout(() => {
      clearInterval(this.preTimer);
      this.preloading = false;
      this.loading = true;         // ahora estamos en la petición real
      this.startNetworkProgress(); // sigue animando hasta ~95%
      this.login();                // dispara el login real
    }, preMs);
  }

  // Progreso "indeterminado" durante la petición (sube lento hasta 95%)
  private startNetworkProgress() {
    const stepMs = 200;
    const inc = 2; // +2% cada 200ms
    this.loadTimer = setInterval(() => {
      if (this.progress < 95) this.progress = Math.min(this.progress + inc, 95);
    }, stepMs);
  }

  // Llama esto al finalizar (éxito o error) para cerrar la animación
  private finishProgress() {
    clearInterval(this.preTimer);
    clearInterval(this.loadTimer);
    this.progress = 100;           // completa visualmente
    setTimeout(() => {
      this.showBar = false;
      this.loading = false;
      this.progress = 0;
    }, 300); // pequeño respiro visual
  }

  // ngOnDestroy() {
  //   if (this.preTimer) clearInterval(this.preTimer);
  //   if (this.loadTimer) clearInterval(this.loadTimer);
  // }

  ngOnDestroy(): void {
    if (this.preTimer) {
      clearInterval(this.preTimer);
    }

    if (this.loadTimer) {
      clearInterval(this.loadTimer);
    }

    if (this.resendTimer) {
      clearInterval(this.resendTimer);
    }
  }


  // contador de tiempo para reenvío de código
  private startResendCountdown(seconds: number): void {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }

    this.resendSeconds = Math.max(
      0,
      Math.ceil(seconds)
    );

    if (this.resendSeconds === 0) {
      return;
    }

    this.resendTimer = setInterval(() => {
      this.resendSeconds--;

      if (this.resendSeconds <= 0) {
        this.resendSeconds = 0;

        if (this.resendTimer) {
          clearInterval(this.resendTimer);
          this.resendTimer = null;
        }
      }
    }, 1000);
  }

  // Formateador del contador de reenvío (mm:ss)
  get resendCountdownLabel(): string {
    const minutes = Math.floor(this.resendSeconds / 60);
    const seconds = this.resendSeconds % 60;

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  // Resetea el flujo de 2FA 
  private resetTwoFactorFlow(message: string): void {
    this.authService.clearTwoFactorState();

    this.requires2fa = false;
    this.verificationCode = '';
    this.remainingAttempts = null;
    this.resendSeconds = 0;

    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }

    this.errorMessage = message;
    this.messageType = 'error';

    this.finishProgress();
  }

}
